/**
 * client.ts
 *
 * Dexie database instance — the single connection to IndexedDB.
 * Import `appDB` anywhere you need to read or write to IndexedDB.
 *
 * VERSION HISTORY:
 *   v1 — initial schema: blocks, sidebarOrder, workAreas, syncQueue, syncMeta
 *   v2 — added dataSources table (replaces boardContext dataSources state)
 *   v3 — migrate isSync from boolean → numeric (0/1)
 *   v4 — added pageCache table for LRU eviction tracking
 *   v5 — added idbProbe table for the startup canary write/delete check
 */

import Dexie, { type Table } from "dexie";
import type {
  BlockRecord,
  SidebarOrderRecord,
  WorkAreaRecord,
  SyncQueueRecord,
  SyncMetaRecord,
  DataSourceRecord,
  PageCacheRecord,
  IdbProbeRecord,
} from "./schema";

class AppDatabase extends Dexie {
  // Each property maps to one object store
  blocks!: Table<BlockRecord, string>;              // keyed by _id
  sidebarOrder!: Table<SidebarOrderRecord, string>; // keyed by id ("global")
  workAreas!: Table<WorkAreaRecord, string>;        // keyed by _id
  syncQueue!: Table<SyncQueueRecord, string>;       // keyed by id (UUID)
  syncMeta!: Table<SyncMetaRecord, string>;         // keyed by id ("global")
  dataSources!: Table<DataSourceRecord, string>;    // keyed by _id
  pageCache!: Table<PageCacheRecord, string>;       // keyed by pageId
  idbProbe!: Table<IdbProbeRecord, string>;         // keyed by id (sentinel string)

  constructor() {
    super("appDB");

    this.version(1).stores({
      /**
       * blocks
       * Primary key: _id
       * Indexes:
       *   workspaceId       → query all blocks for a workspace
       *   parentId          → get children of any block
       *   isSync            → watcher queries: WHERE isSync: 0 | 1 = 0
       *   status            → filter alive/deleted/archived blocks
       *   blockType         → filter pages vs content blocks
       *   [workspaceId+isSync] → compound: watcher queries unsynced per workspace
       */
      blocks: "_id, workspaceId, parentId, isSync, status, blockType, [workspaceId+isSync]",

      /**
       * sidebarOrder
       * Primary key: id
       * No indexes needed — always read as single "global" record
       */
      sidebarOrder: "id",

      /**
       * workAreas
       * Primary key: _id
       * Indexes:
       *   workspaceId → get all work areas for a workspace
       *   isSync      → watcher queries
       */
      workAreas: "_id, workspaceId, isSync",

      /**
       * syncQueue
       * Primary key: id
       * Indexes:
       *   isSync      → watcher primary query: WHERE isSync: 0 | 1 = 0
       *   createdAt   → ordering (watcher always processes oldest first)
       *   noteId      → per-note pending ops lookup
       *   workspaceId → workspace-scoped queries
       *   operationType → filter by op type
       */
      syncQueue: "id, isSync, createdAt, noteId, workspaceId, operationType",

      /**
       * syncMeta
       * Primary key: id
       * No indexes needed — always read as single "global" record
       */
      syncMeta: "id",
    });

    // v2 — only declares the NEW store; Dexie keeps v1 stores untouched
    this.version(2).stores({
      /**
       * dataSources
       * Primary key: _id
       * Indexes:
       *   workspaceId → get all datasources for a workspace
       *   isSync      → watcher queries
       */
      dataSources: "_id, workspaceId, isSync",
    });

    // v3 — migrate isSync from boolean (true/false) to numeric (0/1)
    // Existing IndexedDB databases may contain isSync: true/false from before
    // the type change. Without this migration, === 0 comparisons silently skip
    // unsynced records, causing permanent data orphaning.
    this.version(3).upgrade(async (tx) => {
      const tables = [
        tx.table("blocks"),
        tx.table("sidebarOrder"),
        tx.table("workAreas"),
        tx.table("syncQueue"),
        tx.table("dataSources"),
      ];

      for (const table of tables) {
        await table.toCollection().modify((record: any) => {
          if (record.isSync === true) {
            record.isSync = 1;
          } else if (record.isSync === false) {
            record.isSync = 0;
          }
        });
      }
    });

    // v4 — add pageCache table for LRU eviction tracking.
    // Stores one record per cached page: pageId, lastAccessedAt, and blockIds.
    // No data migration needed — starts empty and populates on first page open.
    this.version(4).stores({
      /**
       * pageCache
       * Primary key: pageId
       * Indexes:
       *   lastAccessedAt → ORDER BY for LRU eviction (oldest first)
       */
      pageCache: "pageId, lastAccessedAt",
    });

    // v5 — add a dedicated idbProbe table used by probeIDB() at startup.
    // Splitting it out keeps syncMeta strictly typed and gives the probe a
    // store with no other contention.
    this.version(5).stores({
      /**
       * idbProbe
       * Primary key: id
       * No indexes — only ever holds a transient sentinel during probe.
       */
      idbProbe: "id",
    });
  }
}

// Singleton instance — import this everywhere
export const appDB = new AppDatabase();

/**
 * Clears the sidebar order from IDB when the user switches workspace.
 * sidebarOrder is a single "global" record shared across workspaces — deleting
 * it prevents the previous workspace's page order from showing until the new
 * workspace's data is fetched and written.
 */
export async function clearWorkspaceIDBData(): Promise<void> {
  try {
    await appDB.sidebarOrder.delete("global");
  } catch (err) {
    // Best-effort — if IDB is unavailable the workspace switch still proceeds
    console.warn("[IDB] clearWorkspaceIDBData failed:", err);
  }
}

/**
 * Initializes the syncMeta record on first run.
 * Safe to call multiple times — only creates if it doesn't exist.
 */
export async function initializeSyncMeta(workspaceId: string): Promise<void> {
  const existing = await appDB.syncMeta.get("global");
  if (!existing) {
    await appDB.syncMeta.put({
      id: "global",
      syncStatus: "idle",
      pendingCount: 0,
      lastSuccessfulSyncAt: null,
      clientId: crypto.randomUUID(),
      workspaceId,
    });
  } else if (existing.workspaceId !== workspaceId) {
    // Workspace changed — update the active workspace reference
    await appDB.syncMeta.update("global", { workspaceId });
  }
}

/**
 * Emits a custom DOM event after any IndexedDB write.
 * All live hooks listen for this to know when to re-query.
 *
 * Usage: call emitIDBChange() at the end of every mutationService function.
 */
export function emitIDBChange(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("idb:changed"));
  }
}
